// ======================================================================
// 📁 File: src/modules/ppe/components/process/ProcessDrawer.jsx
// 📝 Purpose:
//   Process Master – Create / Edit Drawer
//   - Code, Name
//   - Setup / Run / Move Time
//   - Standard Scrap %
//   - POST / PUT API integration
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../api/axiosClient";

export default function ProcessDrawer({
  open,
  mode = "create", // create | edit
  record = null,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    process_code: "",
    process_name: "",
    setup_time_minutes: "",
    run_time_minutes: "",
    move_time_minutes: "",
    standard_scrap_percent: "",
    is_active: true,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ------------------------------------------------------------
  // Initialize form
  // ------------------------------------------------------------
  useEffect(() => {
    if (mode === "edit" && record) {
      setForm({
        process_code: record.process_code || "",
        process_name: record.process_name || "",
        setup_time_minutes:
          record.setup_time_minutes ?? "",
        run_time_minutes:
          record.run_time_minutes ?? "",
        move_time_minutes:
          record.move_time_minutes ?? "",
        standard_scrap_percent:
          record.standard_scrap_percent ?? "",
        is_active:
          record.is_active !== undefined
            ? record.is_active
            : true,
      });
    } else {
      setForm({
        process_code: "",
        process_name: "",
        setup_time_minutes: "",
        run_time_minutes: "",
        move_time_minutes: "",
        standard_scrap_percent: "",
        is_active: true,
      });
    }
  }, [mode, record]);

  if (!open) return null;

  const onChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  };

  // ------------------------------------------------------------
  // Submit handler
  // ------------------------------------------------------------
  const onSubmit = async () => {
    setError(null);

    if (!form.process_code || !form.process_name || !form.run_time_minutes) {
      setError("Code, Name, and Run Time are mandatory");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        process_code: form.process_code,
        process_name: form.process_name,
        setup_time_minutes:
          Number(form.setup_time_minutes) || 0,
        run_time_minutes:
          Number(form.run_time_minutes),
        move_time_minutes:
          Number(form.move_time_minutes) || 0,
        standard_scrap_percent:
          Number(form.standard_scrap_percent) || 0,
      };

      // is_active ONLY allowed in edit
      if (mode === "edit") {
        payload.is_active = form.is_active;
      }

      if (mode === "create") {
        await axiosClient.post(
          "/api/ppe/masters/processes",
          payload
        );
      }

      if (mode === "edit" && record?.id) {
        await axiosClient.put(
          `/api/ppe/masters/processes/${record.id}`,
          payload
        );
      }

      onSaved?.();
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 420,
        height: "100vh",
        background: "#fff",
        boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          padding: 16,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h6 style={{ margin: 0 }}>
          {mode === "create"
            ? "Create Process"
            : "Edit Process"}
        </h6>
        <button
          className="btn btn-sm btn-light"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {/* ====================================================== */}
      {/* Body */}
      {/* ====================================================== */}
      <div
        style={{
          padding: 16,
          flex: 1,
          overflowY: "auto",
        }}
      >
        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        <div className="mb-3">
          <label className="form-label">
            Process Code *
          </label>
          <input
            className="form-control"
            value={form.process_code}
            onChange={(e) =>
              onChange("process_code", e.target.value)
            }
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Process Name *
          </label>
          <input
            className="form-control"
            value={form.process_name}
            onChange={(e) =>
              onChange("process_name", e.target.value)
            }
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Setup Time (minutes)
          </label>
          <input
            type="number"
            className="form-control"
            value={form.setup_time_minutes}
            onChange={(e) =>
              onChange(
                "setup_time_minutes",
                e.target.value
              )
            }
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Run Time (minutes) *
          </label>
          <input
            type="number"
            className="form-control"
            value={form.run_time_minutes}
            onChange={(e) =>
              onChange(
                "run_time_minutes",
                e.target.value
              )
            }
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Move Time (minutes)
          </label>
          <input
            type="number"
            className="form-control"
            value={form.move_time_minutes}
            onChange={(e) =>
              onChange(
                "move_time_minutes",
                e.target.value
              )
            }
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Standard Scrap (%)
          </label>
          <input
            type="number"
            step="0.01"
            className="form-control"
            value={form.standard_scrap_percent}
            onChange={(e) =>
              onChange(
                "standard_scrap_percent",
                e.target.value
              )
            }
          />
        </div>

        {mode === "edit" && (
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={form.is_active}
              onChange={(e) =>
                onChange("is_active", e.target.checked)
              }
              id="processActive"
            />
            <label
              className="form-check-label"
              htmlFor="processActive"
            >
              Active
            </label>
          </div>
        )}
      </div>

      {/* ====================================================== */}
      {/* Footer */}
      {/* ====================================================== */}
      <div
        style={{
          padding: 16,
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <button
          className="btn btn-outline-secondary"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={saving}
          onClick={onSubmit}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
